import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { type DataColumn } from "../components/DataTable/DataTable";
import LineChart from "../components/LineChart/LineChart";
import EmptyState from "../components/EmptyState/EmptyState";
import useTrackingAggregate from "../hooks/useTrackingAggregate";
import { useSingleSurfaceResolver } from "../hooks/useEntityResolver";
import { adminRouteForSurface } from "../utils/surfaceAdminRoutes";
import {
	clickEvent,
	CONTENT_SURFACES,
	impressionEvent,
	type ContentSurface
} from "../utils/eventNames";
import { autoBucketFor, formatRange, type Period } from "../utils/period";
import { slugForFilename } from "../utils/csv";
import { makeTablistKeyHandler } from "../utils/tablistKeyboard";
import usePermissions from "src/hooks/usePermissions/usePermissions";
import { Permissions } from "src/constants/permissions";
import EntityAudiencePanel from "./EntityAudiencePanel";

interface Props {
	period: Period;
	availableEvents: Set<string>;
}

interface Row {
	entity_id: string;
	/** Resolved title — falls back to the raw entity_id while loading. */
	title: string;
	/** Resolved numeric admin id, used to build a clickable link. */
	resolved_id: number | null;
	impressions: number;
	/** Distinct people reached — from the impression bucket's distinct_user_count. */
	people: number;
	clicks: number;
	ctr: number;
}

const ContentTab: FC<Props> = ({ period, availableEvents }) => {
	const navigate = useNavigate();
	const { hasPermission } = usePermissions();
	const canViewUsers = hasPermission(Permissions.CanViewUserAnalytics);
	const [surface, setSurface] = useState<ContentSurface>("CAROUSEL");
	const [audienceRow, setAudienceRow] = useState<Row | null>(null);

	useEffect(() => { setAudienceRow(null); }, [surface]);
	const impEvent = impressionEvent(surface);
	const clkEvent = clickEvent(surface);
	const impAvail = availableEvents.has(impEvent);
	const clkAvail = availableEvents.has(clkEvent);

	const impressions = useTrackingAggregate(
		{
			event: impEvent,
			from: period.from,
			to: period.to,
			groupBy: "entityId"
		},
		{ enabled: impAvail }
	);
	const clicks = useTrackingAggregate(
		{
			event: clkEvent,
			from: period.from,
			to: period.to,
			groupBy: "entityId"
		},
		{ enabled: clkAvail }
	);
	const trend = useTrackingAggregate(
		{
			event: impEvent,
			from: period.from,
			to: period.to,
			groupBy: autoBucketFor(period)
		},
		{ enabled: impAvail }
	);

	const entityIds = useMemo(() => {
		const ids = new Set<string>();
		(impressions.data?.buckets ?? []).forEach((b) => ids.add(b.bucket));
		(clicks.data?.buckets ?? []).forEach((b) => ids.add(b.bucket));
		return Array.from(ids).filter((id) => id && id !== "(null)");
	}, [impressions.data, clicks.data]);

	const resolver = useSingleSurfaceResolver(surface, entityIds, {
		enabled: impAvail || clkAvail
	});

	const rows: Row[] = useMemo(() => {
		const blank = (entityId: string): Row => {
			const resolved = resolver.lookup[entityId];
			return {
				entity_id: entityId,
				title: resolved?.title ?? entityId ?? "(none)",
				resolved_id: resolved?.entity_id ?? null,
				impressions: 0,
				people: 0,
				clicks: 0,
				ctr: 0
			};
		};
		const map = new Map<string, Row>();
		(impressions.data?.buckets ?? []).forEach((b) => {
			const row = map.get(b.bucket) ?? blank(b.bucket);
			row.impressions = b.count;
			row.people = b.distinct_user_count ?? 0;
			map.set(b.bucket, row);
		});
		(clicks.data?.buckets ?? []).forEach((b) => {
			const row = map.get(b.bucket) ?? blank(b.bucket);
			row.clicks = b.count;
			map.set(b.bucket, row);
		});
		const out = Array.from(map.values());
		for (const r of out) {
			r.ctr = r.impressions > 0 ? r.clicks / r.impressions : 0;
		}
		out.sort((a, b) => b.impressions - a.impressions);
		return out;
	}, [impressions.data, clicks.data, resolver.lookup]);

	const surfaceLabel =
		CONTENT_SURFACES.find((s) => s.key === surface)?.label ?? "Entity";

	const columns: DataColumn<Row>[] = [
		{
			key: "entity",
			header: surfaceLabel,
			render: (r) => r.title || "(none)",
			csv: (r) => r.title || r.entity_id
		},
		{
			key: "impressions",
			header: "Impressions",
			align: "right",
			render: (r) => r.impressions.toLocaleString(),
			csv: (r) => r.impressions
		},
		{
			key: "people",
			header: "People",
			align: "right",
			render: (r) =>
				canViewUsers && r.people > 0 ? (
					<button
						type="button"
						className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
						onClick={(e) => {
							e.stopPropagation();
							setAudienceRow(r);
						}}
						onKeyDown={(e) => {
							// The enclosing DataTable row is keyboard-activatable
							// (Enter/Space → onRowClick → navigate). Stop the keydown
							// bubbling so activating this button doesn't also navigate
							// the row; the native button activation still fires.
							if (e.key === "Enter" || e.key === " ") {
								e.stopPropagation();
							}
						}}
					>
						{r.people.toLocaleString()}
					</button>
				) : (
					r.people.toLocaleString()
				),
			csv: (r) => r.people
		},
		{
			key: "clicks",
			header: "Clicks",
			align: "right",
			render: (r) => r.clicks.toLocaleString(),
			csv: (r) => r.clicks
		},
		{
			key: "ctr",
			header: "CTR",
			align: "right",
			render: (r) => `${(r.ctr * 100).toFixed(1)}%`,
			csv: (r) => (r.ctr * 100).toFixed(2)
		}
	];

	return (
		<div className="space-y-6">
			<div
				className="inline-flex flex-wrap bg-white border border-border p-1 rounded-lg shadow-xs"
				role="tablist"
				aria-label="Content surface"
				onKeyDown={makeTablistKeyHandler<ContentSurface>(
					CONTENT_SURFACES.map((s) => s.key),
					surface,
					setSurface
				)}
			>
				{CONTENT_SURFACES.map((s) => (
					<button
						key={s.key}
						type="button"
						role="tab"
						aria-selected={surface === s.key}
						tabIndex={surface === s.key ? 0 : -1}
						onClick={() => setSurface(s.key)}
						className={`px-3 py-1.5 text-sm rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
							surface === s.key
								? "bg-primary text-white"
								: "text-zinc-600 hover:text-foreground"
						}`}
					>
						{s.label}
					</button>
				))}
			</div>

			{!impAvail && !clkAvail ? (
				<EmptyState
					title="Tracking events for this surface aren't being emitted yet"
					description={`Expected events: ${impEvent}, ${clkEvent}. Once the mobile clients start firing them, this surface's metrics will populate.`}
					tone="warn"
				/>
			) : (
				<>
					<DataTable
						caption={`Entities by impressions — ${period.label}`}
						rows={rows}
						columns={columns}
						rowKey={(r) => r.entity_id}
						onRowClick={(r) => {
							if (r.resolved_id === null) return;
							const route = adminRouteForSurface(surface, r.resolved_id);
							if (route) navigate(route);
						}}
						loading={
							impressions.isLoading || clicks.isLoading || resolver.isLoading
						}
						csvFilename={`content-${slugForFilename(
							surface
						)}-${slugForFilename(formatRange(period.from, period.to))}.csv`}
						pagination={{ pageSize: 10, itemLabel: "entities" }}
					/>
					<div className="rounded-xl border border-border bg-white shadow-sm p-4">
						<h3 className="font-heading text-base font-medium mb-3">
							Impressions over time
						</h3>
						{trend.isLoading ? (
							<div className="h-40 animate-pulse bg-zinc-100 rounded" />
						) : (
							<LineChart
								ariaLabel={`${surface} impressions over time`}
								yLabel="Impressions"
								series={[
									{
										label: "Impressions",
										color: "var(--color-primary)",
										data: (trend.data?.buckets ?? []).map((b) => ({
											x: b.bucket,
											y: b.count
										}))
									}
								]}
							/>
						)}
					</div>
					{audienceRow ? (
						<EntityAudiencePanel
							surface={surface}
							entityId={audienceRow.entity_id}
							title={audienceRow.title || audienceRow.entity_id}
							period={period}
							onClose={() => setAudienceRow(null)}
						/>
					) : null}
				</>
			)}
		</div>
	);
};

export default ContentTab;
